import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { Row, Col, CardTitle, Button, Table, Input, Form, FormGroup, Modal, ModalBody, Spinner, Tooltip } from "reactstrap";
import Pagination from "react-js-pagination";
import Select from "react-select";
import ReactDatetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo, faSort, faSortUp, faSortDown, faTimes, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import footballService from "../../services/api/football-service.js";

const SortableHeader = ({ children, columnKey, sortBy, sortOrder, onSort }) => {
  const isActive = sortBy === columnKey;
  let icon = faSort;
  if (isActive) {
    icon = sortOrder === "asc" ? faSortUp : faSortDown;
  }
  return (
    <div
      className="d-flex align-items-center cursor-pointer select-none text-nowrap user-select-none"
      onClick={() => onSort(columnKey)}
    >
      <span className="me-2">{children}</span>
      <FontAwesomeIcon icon={icon} className="text-muted" />
    </div>
  );
};

function TournamentsPage({ refetchTrigger }) {
  const history = useHistory();
  const [tournaments, setTournaments] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTournaments, setTotalTournaments] = useState(0);
  const [pageSize] = useState(10);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [activeTooltipId, setActiveTooltipId] = useState(null);

  const initialFormState = { name: "", competitionSlug: "", fromDate: null, toDate: null, };
  const [formState, setFormState] = useState(initialFormState);

  const records = pageSize;

  const paginationData = useMemo(() => {
    const firstRecord = (currentPage - 1) * records + 1;
    const lastRecord = Math.min(currentPage * records, totalTournaments);
    return { firstRecord, lastRecord };
  }, [currentPage, totalTournaments, records]);

  const fetchTournamentsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfoMessage(null);

    const params = {
      page: currentPage,
      size: pageSize,
      sortBy,
      sortOrder,
    };
    if (formState.name) params.q = formState.name;
    if (formState.competitionSlug) params.competitionSlug = formState.competitionSlug;
    if (formState.fromDate) params.fromDate = moment(formState.fromDate).startOf('day').unix();
    if (formState.toDate) params.toDate = moment(formState.toDate).endOf('day').unix();

    try {
      const data = await footballService.getTournaments(params);
      if (data.status === "syncing") {
        setInfoMessage(data.message);
        setTournaments([]);
      } else if (data.status === "success") {
        setTournaments(data.data);
        setTotalPages(data.totalPages);
        setTotalTournaments(data.total);
      } else {
        throw new Error(data.error || "An unknown error occurred");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || "Failed to fetch data.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, formState, sortBy, sortOrder, pageSize]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      fetchTournamentsData();
    }, 500);
    return () => clearTimeout(debounceTimeout);
  }, [fetchTournamentsData]);

  useEffect(() => {
    if (refetchTrigger > 0) {
      fetchTournamentsData();
    }
  }, [refetchTrigger, fetchTournamentsData]);

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        const result = await footballService.getCompetitions();
        setCompetitions(result.data || []);
      } catch (err) {
        toast.error("Failed to fetch competitions");
      }
    };
    fetchCompetitions();
  }, []);
  
  const toggleTooltip = (id) => {
    setActiveTooltipId(id === activeTooltipId ? null : id);
  };

  const handleFormChange = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFormState(initialFormState);
    setCurrentPage(1);
  }, [initialFormState]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("en-CA");
  };

  const handleModalOpen = useCallback((tournament) => {
    setSelectedTournament(tournament);
    setModalOpen(true);
  }, []);

  const handleSort = (columnKey) => {
    setSortBy(columnKey);
    setSortOrder((prev) => (sortBy === columnKey && prev === "desc" ? "asc" : "desc"));
    setCurrentPage(1);
  };

  const competitionOptions = useMemo(() =>
    competitions.map(comp => ({ label: comp.name, value: comp.slug })),
    [competitions]
  );

  return (
    <>
      <div className="p-4 border-bottom">
        <CardTitle tag="h4" className="mb-3 fw-bold">Filter Tournaments</CardTitle>
        <Form>
          <Row>
            <Col md="3"> <FormGroup><Input type="text" placeholder="Search Tournament Name..." value={formState.name} onChange={(e) => handleFormChange("name", e.target.value)} /></FormGroup></Col>
            <Col md="3"> <FormGroup><Select placeholder="Filter by Competition" options={competitionOptions} value={competitionOptions.find(opt => opt.value === formState.competitionSlug) || null} onChange={(selected) => handleFormChange("competitionSlug", selected ? selected.value : "")} isClearable /></FormGroup></Col>
            <Col md="3"> <FormGroup><ReactDatetime value={formState.fromDate} onChange={(date) => handleFormChange("fromDate", date)} timeFormat={false} inputProps={{ placeholder: "From Date", className: "form-control" }} /></FormGroup></Col>
            <Col md="3"> <FormGroup><ReactDatetime value={formState.toDate} onChange={(date) => handleFormChange("toDate", date)} timeFormat={false} inputProps={{ placeholder: "To Date", className: "form-control" }} /></FormGroup></Col>
          </Row>
          <Row><Col><Button color="warning" size="sm" type="button" onClick={handleFilterReset}>Reset Filters</Button></Col></Row>
        </Form>
      </div>

      <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
        <div className="text-muted small">{totalTournaments > 0 ? `Showing ${paginationData.firstRecord} - ${paginationData.lastRecord} of ${totalTournaments}` : 'No tournaments found'}</div>
        <Pagination activePage={currentPage} itemsCountPerPage={records} totalItemsCount={totalTournaments} pageRangeDisplayed={3} onChange={setCurrentPage} itemClass="page-item" linkClass="page-link" />
      </div>

      <div className="table-responsive">
        <Table className="table-hover table-striped mb-0">
          <thead className="text-dark bg-light small">
            <tr>
              <th className="px-4">S.No</th>
              <th className="px-4"><SortableHeader columnKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Tournament</SortableHeader></th>
              <th className="px-4">Competition</th>
              <th className="px-4"><SortableHeader columnKey="start_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Start Date</SortableHeader></th>
              <th className="px-4"><SortableHeader columnKey="end_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>End Date</SortableHeader></th>
              <th className="text-center px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="text-center py-5"><Spinner /></td></tr>
            ) : error ? (
              <tr><td colSpan="6" className="text-center text-danger py-5">{error}</td></tr>
            ) : infoMessage ? (
              <tr><td colSpan="6" className="text-center text-info py-5">{infoMessage}</td></tr>
            ) : tournaments.length > 0 ? (
              tournaments.map((tourney, index) => (
                <tr key={tourney.t_slug}>
                  <td className="px-4">{(currentPage - 1) * records + index + 1}</td>
                  <td className="px-4 fw-bold">{tourney.name}</td>
                  <td className="px-4">{tourney.competition?.name || "N/A"}</td>
                  <td className="px-4">{formatDate(tourney.start_date?.timestamp)}</td>
                  <td className="px-4">{formatDate(tourney.end_date?.timestamp)}</td>
                  <td className="text-center px-4">
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <Button color="danger #f97316" size="sm" className="btn-icon-only rounded-circle" id={`infoBtn_${tourney.t_slug}`} onClick={() => handleModalOpen(tourney)} style={{ padding: '0.4rem', width: '2rem', height: '2rem' }}>
                            <FontAwesomeIcon icon={faInfo} />
                        </Button>
                        <Tooltip placement="top" isOpen={activeTooltipId === `infoBtn_${tourney.t_slug}`} target={`infoBtn_${tourney.t_slug}`} toggle={() => toggleTooltip(`infoBtn_${tourney.t_slug}`)}>
                            Detailed View
                        </Tooltip>
                        
                        <Button color="success" size="sm" className="btn-icon-only rounded-circle" id={`detailsBtn_${tourney.t_slug}`} onClick={() => history.replace(`/dashboard/football/tournament/${tourney.t_slug}`)} style={{ padding: '0.4rem', width: '2rem', height: '2rem' }}>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Button>
                         <Tooltip placement="top" isOpen={activeTooltipId === `detailsBtn_${tourney.t_slug}`} target={`detailsBtn_${tourney.t_slug}`} toggle={() => toggleTooltip(`detailsBtn_${tourney.t_slug}`)}>
                            Go to Details
                        </Tooltip>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" className="text-center py-5"><h4 className="text-muted">No Tournaments Found</h4></td></tr>
            )}
          </tbody>
        </Table>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="md" centered>
        <ModalBody className="p-3 position-relative">
          <div className="position-absolute d-flex align-items-center justify-content-center" style={{ right: "12px", top: "8px", cursor: "pointer", zIndex: 1, backgroundColor: "red", borderRadius: "50%", width: "25px", height: "25px", }} onClick={() => setModalOpen(false)}>
            <FontAwesomeIcon icon={faTimes} className="text-white" />
          </div>
          <div className="mb-3">
            <h6 className="mb-2">Tournament Details</h6>
            <div className="border rounded">
              {[
                { label: "Name", value: selectedTournament?.name },
                { label: "Competition", value: selectedTournament?.competition?.name },
                { label: "Start Date", value: moment(selectedTournament?.start_date?.timestamp * 1000).format("MMMM D, YYYY") },
                { label: "End Date", value: moment(selectedTournament?.end_date?.timestamp * 1000).format("MMMM D, YYYY") },
                { label: "Slug", value: selectedTournament?.t_slug },
              ].map((item, index) => (
                <Row key={index} className={`g-0 ${index > 0 ? "border-top" : ""}`}>
                  <Col xs="5" className="p-2 bg-light"><div className="fw-semibold text-dark">{item.label}</div></Col>
                  <Col className="p-2"><div className="fw-semibold">{item.value || 'N/A'}</div></Col>
                </Row>
              ))}
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default TournamentsPage;